import { format } from "date-fns";

import { DiabetesTypeOptionId, HealthConditionOptionId, PregnantSelection } from "@/constants";
import type { Partner, User } from "@/models";
import { getRegionAbbreviation } from "@/utils/location";

export type MedEnrollmentData = {
  userId: string;
  accessCode: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  email: string;
  address1: string;
  address2: string;
  city: string;
  stateCode: string;
  zipCode: string;
  phoneNumber: string;
  mainReason: string;
  familyHistory: string;
  pregnant: PregnantSelection;
  healthConditions: string[];
  heightFeet: number;
  heightInch: number;
  weightLbs: number;
  partnerId: number;
  useMedications: string;
  suicideOrSelfHarm1: string;
  suicideOrSelfHarm2: string;
  glp1Medication: string;
};

export const formatAsMedEnrollmentData = ({
  accessCode,
  b2bIdentifiedUserId,
  partner,
  userData,
}: {
  accessCode: string;
  b2bIdentifiedUserId: string;
  partner: Partner;
  userData: User;
}): MedEnrollmentData => {
  const { id: partnerId } = partner;
  // using phoneNumber from userData (vs surveyAnswers) as there is already formatting applied.
  const {
    extras,
    phoneNumber,
    surveyAnswers,
    gender: userDataGender,
  } = userData;
  const {
    diabetesType,
    eligibilityCheck,
    gender: surveyGender,
    createAccount,
    mainReason,
    familyHistoryWeight,
    pregnant,
    healthConditions,
    heightWeight,
    medications,
    phq3,
    phq4,
    glp1Medication,
  } = surveyAnswers!;
  const { firstName, lastName, dob } = eligibilityCheck!;
  // reading address from extras (vs surveyAnswers) as there is validation / correction applied.
  const { address1, address2, city, region, zipcode, country } = extras.address;
  const stateCode = getRegionAbbreviation(country, region)!;

  const { heightFeetPortion, heightInchesPortion, weight } = heightWeight!;

  // Map diabetesType to appropriate HealthConditionOptionId if applicable.
  // This is necessary because in the diabetes-program-triage experiment,
  // the diabetes options are not shown in the health conditions question,
  // so we need to add the diabetes type to the health conditions array
  // if the user has diabetes.
  const finalHealthConditions = [...(healthConditions ?? [])];
  if (
    diabetesType === DiabetesTypeOptionId.TYPE1 &&
    !finalHealthConditions.includes(HealthConditionOptionId.TYPE1_DIABETES)
  ) {
    finalHealthConditions.push(HealthConditionOptionId.TYPE1_DIABETES);
  } else if (
    diabetesType === DiabetesTypeOptionId.TYPE2 &&
    !finalHealthConditions.includes(HealthConditionOptionId.TYPE2_DIABETES)
  ) {
    finalHealthConditions.push(HealthConditionOptionId.TYPE2_DIABETES);
  }

  // For FEHBP we do not ask the gender question, so we must pass the value from
  // the userData which has been mapped to gender based on the user's answer to
  // the sex question
  const gender = surveyGender || userDataGender?.toLowerCase() || "other";

  return {
    userId: b2bIdentifiedUserId,
    accessCode,
    firstName,
    lastName,
    dateOfBirth: format(dob!, "yyyy-MM-dd"),
    gender,
    email: createAccount!.email,
    address1,
    address2,
    city,
    stateCode,
    zipCode: zipcode,
    phoneNumber: phoneNumber!,
    mainReason: mainReason!,
    familyHistory: familyHistoryWeight!,
    pregnant: pregnant || PregnantSelection.NO,
    healthConditions: finalHealthConditions,
    heightFeet: heightFeetPortion!,
    heightInch: heightInchesPortion!,
    weightLbs: weight!,
    partnerId,
    useMedications: Number(medications) > 0 ? "yes" : "no",
    suicideOrSelfHarm1: phq4!,
    suicideOrSelfHarm2: phq3!,
    glp1Medication: glp1Medication ?? "no",
  };
};
